import React, {useContext, useMemo} from 'react';
import {List} from 'immutable';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import constants from 'client/constants';
import {renderMoment} from 'lib/timeUtils';
import {
  Anchor,
  copyToClipboard,
  addToast,
  Text,
  Icon,
  WithTooltip,
  IconButton,
  Heading
} from '@albert-io/atomic';

import {
  GoogleContext,
  GoogleSignInButton,
  GoogleSwitchAccountsButton
} from 'client/components/Google';

import {LtiCell} from './LtiCell.react';
import {GoogleCell} from './GoogleCell.react';

import './assignment-sharing.scss';

import {pluralize} from 'lib/stringUtils';
import classnames from 'classnames';

type GoogleContextType = {
  isRequestPending: boolean;
  isSignedIn: boolean;
  handleExportAssignment: (...args: any[]) => void;
};

interface ShareAssignmentListProps {
  assignments: List<AssignmentModelV3>;
}

export const ShareAssignmentList = ({assignments = List()}: ShareAssignmentListProps) => {
  const {isRequestPending, isSignedIn} = useContext<GoogleContextType>(GoogleContext);

  const someSchoolHasLtiDeployment = assignments.some((assignment) => {
    const classroom = assignment.getClassrooms().first();
    return classroom.getMeta().hasLtiDeployment();
  });
  const tableHeaderClassName = classnames('assignment-sharing__table--header', {
    with_lms: someSchoolHasLtiDeployment
  });

  const sortedAssignments = useMemo(() => {
    return assignments.sort((a, b) => {
      const nameA = a.getClassrooms().first().getName();
      const nameB = b.getClassrooms().first().getName();
      return nameA.localeCompare(nameB);
    });
  }, [assignments]);

  return (
    <div className='assignment-sharing__container'>
      <div className='assignment-sharing__header u-mar-b_5 u-display_flex u-align-items_center u-gap_2'>
        <Heading
          size='xs'
          className='u-bgc_green-600 u-color_white u-border-radius_pill u-display_inline-block u-pad-lr_2 u-pad-tb_1'
        >
          <Icon icon='check' className='u-mar-r_1' size='inherit' />
          Assigned
        </Heading>
        <Heading>{sortedAssignments.first().getName()}</Heading>
      </div>
      <div className='assignment-sharing__table'>
        <div className={tableHeaderClassName}>
          <Text bold>Classroom</Text>
          <Text bold>Student Links</Text>
          <div className='u-display_flex u-flex-direction_column u-align-items_center'>
            <Text bold className=''>
              Post to Google Classroom
            </Text>
            <div>
              {isSignedIn ? (
                <GoogleSwitchAccountsButton
                  hideIcon
                  disabled={isRequestPending}
                  variant='text'
                  size='s'
                />
              ) : (
                <GoogleSignInButton hideIcon text='Sign In To Google' variant='text' size='s' />
              )}
            </div>
          </div>
          {someSchoolHasLtiDeployment && (
            <Text bold className='u-justify-self_center'>
              Sync Grades With LMS
            </Text>
          )}
        </div>
        <div className='assignment-sharing__table--body'>
          {sortedAssignments.map((assignment) => (
            <ShareAssignmentListItem
              key={assignment.getId()}
              assignment={assignment}
              someSchoolHasLtiDeployment={someSchoolHasLtiDeployment}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface ShareAssignmentListItemProps {
  assignment: AssignmentModelV3;
  someSchoolHasLtiDeployment: boolean;
}

const ShareAssignmentListItem = ({
  assignment,
  someSchoolHasLtiDeployment
}: ShareAssignmentListItemProps) => {
  const classroom = assignment.getClassrooms().first();
  const assignmentSize = assignment.getMeta().getCountOfStudents();
  const classroomSize = classroom.getMeta().getCountOfStudents();

  const urlPrefix = constants.ALBERT_FULL_PATH;
  const assignmentUrl = `${urlPrefix}/assignment/${assignment.getId()}`;
  const classroomUrl = `${urlPrefix}/classes/${classroom.getId()}/assignments`;

  const tableRowClassName = classnames('assignment-sharing__table--row', {
    with_lms: someSchoolHasLtiDeployment
  });

  const copyShareLink = () => {
    copyToClipboard(assignmentUrl, () => {
      addToast({
        type: 'success',
        message: 'Link copied to clipboard.'
      });
    });
  };

  const getStudentCountText = () => {
    if (assignmentSize === 0) {
      return 'No Students';
    }
    if (assignmentSize === classroomSize) {
      return 'All Students';
    }
    return `${assignmentSize} ${pluralize('Student', assignmentSize)}`;
  };

  return (
    <div className={tableRowClassName}>
      <div className='u-display_flex u-flex-direction_column'>
        <Anchor
          className='assignment-sharing-_assignment-name-link'
          underlined
          target='_blank'
          rel='noopener noreferrer'
          to={classroomUrl}
        >
          {classroom.getName()}
        </Anchor>
        <Text size='s' className='u-color_slate-500'>
          {getStudentCountText()}, Due {renderMoment(assignment.getDueDate(), 'dateFormal')}
        </Text>
      </div>
      <div className='assignment-sharing__assignment-link'>
        <Text className='assignment-sharing__assignment-link--text'>{assignmentUrl}</Text>
        <WithTooltip content='Copy link'>
          <IconButton
            icon='copy'
            color='primary'
            label='copy assignment link'
            className='u-color_blue-500 assignment-sharing__assignment-link--button'
            onClick={copyShareLink}
          />
        </WithTooltip>
      </div>
      <GoogleCell
        assignment={assignment}
        googleCourseId={classroom.getGoogleCourseId()}
        url={assignmentUrl}
      />
      {someSchoolHasLtiDeployment && <LtiCell assignment={assignment} classroom={classroom} />}
    </div>
  );
};
